/**
 * Проекция координат в viewBox SVG — для схем без движка карты (план
 * 21.09: линия из таблички `&r=` при недошедшем venue.json и аварийная
 * схема площадки, когда чанк MapLibre недоступен). Равнопромежуточная
 * проекция с поправкой на широту: на масштабе площадки (сотни метров)
 * искажения незаметны. Север — вверху, ось Y перевёрнута.
 */
import type { LonLat } from '@app/geo'

export interface SvgBox {
  readonly w: number
  readonly h: number
  /** Поле внутри viewBox, чтобы линии не упирались в край. */
  readonly pad: number
}

export type Projector = (p: LonLat) => readonly [number, number]

/** Минимальный охват (~11 м): одна точка или отрезок в пару метров не
 *  должны раздуваться на весь экран. */
const MIN_SPAN_DEG = 1e-4

/** Проектор, вписывающий все `points` в `box` с сохранением пропорций. */
export function makeProjector(points: readonly LonLat[], box: SvgBox): Projector {
  if (points.length === 0) return () => [box.w / 2, box.h / 2]
  let minLon = Number.POSITIVE_INFINITY
  let maxLon = Number.NEGATIVE_INFINITY
  let minLat = Number.POSITIVE_INFINITY
  let maxLat = Number.NEGATIVE_INFINITY
  for (const [lon, lat] of points) {
    if (lon < minLon) minLon = lon
    if (lon > maxLon) maxLon = lon
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
  }
  const kx = Math.cos((((minLat + maxLat) / 2) * Math.PI) / 180)
  const spanX = Math.max((maxLon - minLon) * kx, MIN_SPAN_DEG)
  const spanY = Math.max(maxLat - minLat, MIN_SPAN_DEG)
  const innerW = box.w - 2 * box.pad
  const innerH = box.h - 2 * box.pad
  const scale = Math.min(innerW / spanX, innerH / spanY)
  const midX = (minLon + maxLon) / 2
  const midY = (minLat + maxLat) / 2
  return ([lon, lat]) => [box.w / 2 + (lon - midX) * kx * scale, box.h / 2 + (midY - lat) * scale]
}

/** Путь SVG `M … L …` по точкам через проектор (пусто — пустая строка). */
export function svgPath(points: readonly LonLat[], project: Projector): string {
  return points
    .map((p, i) => {
      const [x, y] = project(p)
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`
    })
    .join(' ')
}
